import DETAIL_TARGET_FIELDS from './financialDetailTargets.json';
// Presentation helpers for Resultados semanales. Balance keeps its original rows.
const number = value => value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);
const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const id = value => String(value?._id || value || '');
const code = label => normalize(label).match(/^(\d{2,4})\b/)?.[1];
const totalCode = label => normalize(label).match(/^total (\d{2,4})\b/)?.[1];
const flatten = rows => rows.flatMap(row => [row, ...flatten(row.children || [])]);
const sum = values => {
    const amounts = values.map(number).filter(value => value !== null);
    return amounts.length ? Math.round(amounts.reduce((a, b) => a + b, 0) * 100) / 100 : null;
};
const SALES_LABELS = { '48': 'Empaques', '45': 'Alimentos', '41': 'Vending Contado', '42': 'Vending Contrato' };
const SUMMARY_CODES = { 'ingresos totales':'totalIncome', 'costo total de las ventas':'costOfSales', 'beneficio bruto':'grossProfit', 'total gastos operativos (61+62+63)':'operatingExpenses', ebitda:'ebitda', 'utilidad en operacion':'operatingIncome', 'total gastos no operativos (65+66+69)':'nonOperatingExpenses', 'ganancias netas':'netIncome' };
export const FINANCIAL_TARGET_FIELDS = [
    ['48','Empaques'],['45','Alimentos'],['41','Vending Contado'],['42','Vending Contrato'],['40','Coffee & Vending total'],['43','Canal institucional'],['49','Otros ingresos'],['totalIncome','Ingresos totales'],
    ['58','Costos Empaques'],['55','Costos Alimentos'],['51','Costos Snacks y Bebidas'],['52','Costos Café e Insumos'],['50','Costos Coffee & Vending total'],['53','Costos Canal institucional'],['59','Variables de ventas'],['costOfSales','Costo total de ventas'],
    ['61','Planilla y contratos'],['62','Gastos fijos'],['63','Desarrollo de negocios'],['operatingExpenses','Gastos operativos totales'],['65','Financieros e impuestos'],['66','Otros gastos'],['69','Beneficios accionistas'],['nonOperatingExpenses','Gastos no operativos totales'],
    ['grossProfit','Beneficio bruto'],['ebitda','EBITDA'],['operatingIncome','Utilidad en operación'],['netIncome','Ganancias netas'],
    ['margin:empaque','Margen bruto Empaques','%'],['margin:foods','Margen bruto Alimentos','%'],['margin:coffee_vending','Margen bruto Vending','%'],
].filter(([key])=>!['66','69','nonOperatingExpenses','netIncome'].includes(key)).map(([key,label,unit='USD'])=>({key,label,unit})).flatMap(field=>[field,...DETAIL_TARGET_FIELDS.filter(detail=>detail.parent === field.key)]);
const BUDGET_KEYS = new Set(['58','55','51','52','50','53','59','costOfSales','61','62','63','operatingExpenses','65','66','69','nonOperatingExpenses']);

export function configuredFinancialTargets(values = {}, cycle) {
    const weeks = number(cycle?.numberOfWeeks);
    if (!weeks || weeks <= 0) return {};
    return Object.fromEntries(FINANCIAL_TARGET_FIELDS.filter(field => number(values[field.key]) !== null && (Number(values[field.key]) >= 0 || field.allowNegative) && (field.unit !== '%' || Number(values[field.key]) <= 100)).map(field => [field.key, { quarterly:Number(values[field.key]), weekly:field.unit === '%' ? null : Number(values[field.key])/weeks, weeks, unit:field.unit, kind:field.kind || (BUDGET_KEYS.has(field.key) ? 'budget' : 'goal'), sourceTitle:'Meta trimestral para este ciclo y empresa', configured:true }]));
}

// Configured quarterly Forecast amounts drive the management report. Personal
// and team OKR scoring stays unchanged; percentage goals keep their OKR source.
export function resolveFinancialTargets(configured = {}, okr = {}) {
    const result = { ...okr, ...configured };
    for (const [key, target] of Object.entries(okr)) {
        if (key.startsWith('margin:')) result[key] = target;
    }
    return result;
}

// The management P&L ends at operating income. Keep source accounting reports
// intact, and show business margins before expenses rather than after the cutoff.
export function operatingFinancialRows(rows = [], margins) {
    let reachedCutoff = false;
    const visit = nodes => {
        const visible = [];
        for (const row of nodes) {
            if (reachedCutoff) break;
            const label = normalize(row.label), account = code(row.label) || totalCode(row.label);
            if (/^(66|69)/.test(account || '') || ['ganancias netas', 'total gastos no operativos (65+66+69)'].includes(label)) continue;
            const children = visit(row.children || []);
            visible.push({ ...row, children });
            if (label === 'utilidad en operacion' || row.accountCode === 'operatingIncome') reachedCutoff = true;
        }
        return visible;
    };
    const result = visit(rows);
    if (margins?.children?.length) {
        const expenses = result.findIndex(row => normalize(row.label) === 'gastos');
        result.splice(expenses >= 0 ? expenses : 0, 0, margins);
    }
    return result;
}

export function groupFinancialResults(rows = []) {
    const groupAccounts = (items, depth) => {
        const result = [];
        for (let index = 0; index < items.length; index += 1) {
            const row = items[index], account = code(row.label);
            const closing = account ? items.findIndex((candidate, n) => n > index && totalCode(candidate.label) === account) : -1;
            if (closing > index) {
                const total = items[closing];
                const weeklyValues = Array.from({ length: Math.max(row.weeklyValues?.length || 0, total.weeklyValues?.length || 0) }, (_, n) => number(total.weeklyValues?.[n]) ?? number(row.weeklyValues?.[n]));
                result.push({ ...row, label: SALES_LABELS[account] || row.label, accountCode: account, alwaysExpanded: ['40', '50'].includes(account), type: 'section', depth, weeklyValues, amount: sum(weeklyValues), children: groupAccounts(items.slice(index + 1, closing), depth + 1) });
                index = closing;
            } else {
                result.push({ ...row, accountCode: row.accountCode || account || totalCode(row.label) || SUMMARY_CODES[normalize(row.label)], depth, children: groupAccounts(row.children || [], depth + 1) });
            }
        }
        return result;
    };
    return rows.map(row => ({ ...row, alwaysExpanded: Boolean(row.children?.length), children: groupAccounts(row.children || [], 1) }));
}

export function financialWeekIndexes(rows = [], weeks = []) {
    const all = flatten(rows);
    return weeks.reduce((indexes, week, index) => {
        if (all.some(row => number(row.weeklyValues?.[index]) !== null)) indexes.push(index);
        return indexes;
    }, []);
}

// Only currency sales goals covering the entire business unit can become report
// targets. Personal quotas, percentages and stale prose are never added together.
export function financialSalesTargets(keyResults = [], cycle, businessUnit = 'consolidated') {
    const weeks = number(cycle?.numberOfWeeks);
    if (!id(cycle) || !weeks || weeks <= 0) return {};
    const candidates = {};
    const unitCodes = { empaque: '48', foods: '45' };
    const add = (account, weekly, kr) => {
        if (number(weekly) === null || Number(weekly) <= 0) return;
        (candidates[account] ||= []).push({ weekly: Number(weekly), quarterly: Math.round(Number(weekly) * weeks * 100) / 100, weeks, keyResultId: id(kr), sourceTitle: kr.title });
    };
    for (const kr of keyResults) {
        const config = kr.automationConfig || {};
        if (id(kr.cycleId) !== id(cycle) || kr.isActive === false || kr.isDeleted || normalize(kr.unit) !== 'usd' || config.sourceType !== 'quickbooks' || config.metricKey !== 'netSales' || config.aggregationScope !== 'team_total' || (kr.calculationType && !['manual_value', 'threshold_range', 'composite_threshold'].includes(kr.calculationType)) || number(kr.weeklyTarget) === null || Number(kr.weeklyTarget) <= 0) continue;
        const units = [...new Set(config.businessUnits?.length ? config.businessUnits : [config.businessUnit].filter(Boolean))];
        let breakdown = [];
        if (units.length === 1 && unitCodes[units[0]]) breakdown = [{ key: units[0], target: Number(kr.weeklyTarget) }];
        const components = config.compositeConfig?.components;
        if (Array.isArray(components) && components.length === units.length && units.every(unit => components.filter(component => component.key === unit && number(component.target) !== null && Number(component.target) > 0).length === 1)) {
            breakdown = components;
        }
        // Existing Syncro team goal stores its split in this precise weekly title.
        // Require both declared units and exact agreement with the numeric target.
        const split = normalize(kr.title).match(/^meta semanal de (\d+(?:\.\d+)?)k en empaques y (\d+(?:\.\d+)?)k en lacteos$/);
        if (!breakdown.length && split && units.length === 2 && units.includes('empaque') && units.includes('foods')) breakdown = [{ key: 'empaque', target: Number(split[1]) * 1000 }, { key: 'foods', target: Number(split[2]) * 1000 }];
        if (!breakdown.length || Math.abs(breakdown.reduce((total, item) => total + Number(item.target), 0) - Number(kr.weeklyTarget)) > 0.01) continue;
        for (const item of breakdown) {
            if (unitCodes[item.key] && (businessUnit === 'consolidated' || businessUnit === item.key)) add(unitCodes[item.key], item.target, kr);
        }
    }
    return Object.fromEntries(Object.entries(candidates).map(([account, goals]) => {
        const unique = [...new Set(goals.map(goal => goal.quarterly))];
        return [account, unique.length === 1 ? goals[0] : { conflict: true }];
    }));
}

// Reports use inclusive business dates. An in-progress week advances by elapsed
// days, while each complete OKR week retains its full configured weekly goal.
export function elapsedFinancialWeeks(weeks = []) {
    if (!weeks.length) return null;
    const day = value => {
        const literal = String(value || '').slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(literal)) return null;
        const time = Date.parse(`${literal}T00:00:00Z`);
        return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === literal ? time / 86400000 : null;
    };
    let elapsed = 0;
    for (const week of weeks) {
        const start = day(week.startDate), end = day(week.endDate);
        if (start === null || end === null || end < start) return null;
        if (week.status === 'future') continue;
        const cut = day(week.effectiveEndDate || (week.status === 'closed' ? week.endDate : null));
        if (cut === null) return null;
        elapsed += Math.max(0, Math.min(end - start + 1, cut - start + 1)) / (end - start + 1);
    }
    return elapsed;
}

export function financialMarginTargets(keyResults = [], cycle, businessUnit = 'consolidated') {
    const candidates = {};
    for (const kr of keyResults) {
        const config = kr.automationConfig || {};
        if (id(kr.cycleId) !== id(cycle) || kr.isActive === false || kr.isDeleted || kr.unit !== '%' || config.sourceType !== 'quickbooks' || config.metricKey !== 'grossMarginPercentage' || config.aggregationScope !== 'team_total') continue;
        const units = [...new Set(config.businessUnits?.length ? config.businessUnits : [config.businessUnit].filter(Boolean))];
        const components = config.compositeConfig?.components;
        const goals = Array.isArray(components) && components.length ? components : units.length === 1 ? [{ key: units[0], target: kr.weeklyTarget }] : [];
        for (const goal of goals) {
            if (!units.includes(goal.key) || !['empaque', 'foods', 'coffee_vending'].includes(goal.key) || (businessUnit !== 'consolidated' && goal.key !== businessUnit) || number(goal.target) === null || Number(goal.target) < 0 || Number(goal.target) > 100) continue;
            (candidates[`margin:${goal.key}`] ||= []).push({ quarterly: Number(goal.target), unit: '%', keyResultId: id(kr), sourceTitle: kr.title });
        }
    }
    return Object.fromEntries(Object.entries(candidates).map(([key, goals]) => [key, new Set(goals.map(goal => goal.quarterly)).size === 1 ? goals[0] : { conflict: true }]));
}

export function financialMarginRows(summaries = [], weeks = [], businessUnit = 'consolidated') {
    const labels = { empaque: 'Empaques', foods: 'Alimentos', coffee_vending: 'Vending' };
    const units = businessUnit === 'consolidated' ? Object.keys(labels) : Object.keys(labels).filter(key => key === businessUnit);
    const rows = units.map(unit => {
        const observed = weeks.map((week, index) => ({ week, summary: summaries[index]?.profitAndLossByBusinessUnit?.[unit] })).filter(item => item.week.status !== 'future' && item.week.effectiveEndDate);
        const complete = observed.length > 0 && observed.every(item => number(item.summary?.totalIncome) !== null && number(item.summary?.grossProfit) !== null);
        const income = complete ? sum(observed.map(item => item.summary.totalIncome)) : null;
        const profit = complete ? sum(observed.map(item => item.summary.grossProfit)) : null;
        return { key: `margin:${unit}`, accountCode: `margin:${unit}`, label: `Margen bruto · ${labels[unit]}`, unit: '%', type: 'account', depth: 1, children: [],
            weeklyValues: weeks.map((week, index) => { const value = summaries[index]?.profitAndLossByBusinessUnit?.[unit]; const denominator = number(value?.totalIncome), numerator = number(value?.grossProfit); return week.status !== 'future' && denominator > 0 && numerator !== null ? numerator / denominator * 100 : null; }),
            amount: income > 0 && profit !== null ? profit / income * 100 : null,
        };
    });
    return { key: 'business-margins', label: 'Margen bruto por negocio', type: 'section', amount: null, weeklyValues: weeks.map(() => null), children: rows };
}

export function financialTargetProgress(row, targets = {}, weeks = []) {
    const target = targets[row.accountCode];
    const actual = number(row.amount);
    const elapsed = elapsedFinancialWeeks(weeks);
    const expected = target && !target.conflict ? target.unit === '%' ? target.quarterly : elapsed === null ? null : target.weekly * Math.min(target.weeks, elapsed) : null;
    return { target, expected, difference: actual !== null && expected !== null ? actual - expected : null, percentage: target && !target.conflict && target.quarterly !== 0 && actual !== null ? actual / target.quarterly * 100 : null };
}

export function financialResultsCsvRows(rows, weeks, targets) {
    const indexes = financialWeekIndexes(rows, weeks);
    return [
        ['Cuenta / descripción', 'Unidad', ...indexes.map(index => `S${weeks[index].weekNumber} (${weeks[index].startDate} a ${weeks[index].endDate})`), 'Acumulado', 'Meta trimestral / objetivo', 'Meta esperada a la fecha', 'Diferencia vs. esperado ($ / pp)', '% logro / uso de presupuesto'],
        ...flatten(groupFinancialResults(rows)).map(row => {
            const { target, expected, difference, percentage } = financialTargetProgress(row, targets, weeks);
            return [row.label, row.unit || 'USD', ...indexes.map(index => row.weeklyValues?.[index] ?? ''), row.amount ?? '', target?.conflict ? 'Metas en conflicto' : target?.quarterly ?? 'Sin meta', expected ?? '', difference ?? '', percentage === null ? '' : Math.round(percentage * 100) / 100];
        }),
    ];
}
